import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FloorTablePlacement } from '../../types'
import { dispatchCatalogEvent, FLOOR_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { stripUndefined } from './stripUndefined'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const floorPlacementsCacheByPlace: Record<string, FloorTablePlacement[]> = {}
let floorPlacementsUnsubscribe: (() => void) | null = null

function dispatchFloorUpdated(): void {
  dispatchCatalogEvent(FLOOR_UPDATED_EVENT)
}

export const DEFAULT_FLOOR_TABLE_W = 96
export const DEFAULT_FLOOR_TABLE_H = 72

export function floorPlacementId(spaceId: string, tableId: string): string {
  return `${spaceId}_${tableId}`
}

function floorPlacementsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'floorPlacements')
}

function floorPlacementDoc(placeId: string, placementId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'floorPlacements', placementId)
}

function docToPlacement(d: QueryDocumentSnapshot): FloorTablePlacement {
  const data = d.data()
  const spaceId = (data.spaceId as string) ?? ''
  const tableId = (data.tableId as string) ?? ''
  return {
    id: d.id,
    spaceId,
    tableId,
    x: typeof data.x === 'number' && Number.isFinite(data.x) ? data.x : 0,
    y: typeof data.y === 'number' && Number.isFinite(data.y) ? data.y : 0,
    w: typeof data.w === 'number' && Number.isFinite(data.w) ? data.w : undefined,
    h: typeof data.h === 'number' && Number.isFinite(data.h) ? data.h : undefined,
    groupId:
      typeof data.groupId === 'string' && data.groupId.trim() ? data.groupId.trim() : undefined,
  }
}

export async function primeFloorPlacementsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (floorPlacementsUnsubscribe) {
    floorPlacementsUnsubscribe()
    floorPlacementsUnsubscribe = null
  }
  const snap = await getDocs(floorPlacementsRef(placeId))
  floorPlacementsCacheByPlace[placeId] = snap.docs.map(docToPlacement)
  floorPlacementsUnsubscribe = onSnapshot(floorPlacementsRef(placeId), (snapshot) => {
    floorPlacementsCacheByPlace[placeId] = snapshot.docs.map(docToPlacement)
    dispatchFloorUpdated()
  })
}

export function getFloorPlacements(): FloorTablePlacement[] {
  const placeId = getCurrentPlaceId()
  return floorPlacementsCacheByPlace[placeId] ?? []
}

export function getFloorPlacementsForSpace(spaceId: string): FloorTablePlacement[] {
  return getFloorPlacements().filter((p) => p.spaceId === spaceId)
}

export function upsertFloorPlacement(
  placement: Omit<FloorTablePlacement, 'id'> & { id?: string },
): FloorTablePlacement {
  const placeId = getCurrentPlaceId()
  const id = placement.id ?? floorPlacementId(placement.spaceId, placement.tableId)
  const full: FloorTablePlacement = {
    id,
    spaceId: placement.spaceId,
    tableId: placement.tableId,
    x: placement.x,
    y: placement.y,
    w: placement.w,
    h: placement.h,
    groupId: placement.groupId,
  }
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  const idx = cache.findIndex((p) => p.id === id)
  if (idx === -1) {
    floorPlacementsCacheByPlace[placeId] = [...cache, full]
  } else {
    const next = [...cache]
    next[idx] = full
    floorPlacementsCacheByPlace[placeId] = next
  }
  setDoc(floorPlacementDoc(placeId, id), stripUndefined(full)).catch((e) =>
    console.error('Firestore upsertFloorPlacement failed', e),
  )
  dispatchFloorUpdated()
  return full
}

export function updateFloorPlacementPosition(
  id: string,
  x: number,
  y: number,
): FloorTablePlacement | null {
  const placeId = getCurrentPlaceId()
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  const found = cache.find((p) => p.id === id)
  if (!found) return null
  const updated: FloorTablePlacement = { ...found, x, y }
  floorPlacementsCacheByPlace[placeId] = cache.map((p) => (p.id === id ? updated : p))
  setDoc(floorPlacementDoc(placeId, id), stripUndefined(updated)).catch((e) =>
    console.error('Firestore updateFloorPlacementPosition failed', e),
  )
  dispatchFloorUpdated()
  return updated
}

export function deleteFloorPlacement(id: string): FloorTablePlacement[] {
  const placeId = getCurrentPlaceId()
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  floorPlacementsCacheByPlace[placeId] = cache.filter((p) => p.id !== id)
  deleteDoc(floorPlacementDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteFloorPlacement failed', e),
  )
  dispatchFloorUpdated()
  return floorPlacementsCacheByPlace[placeId]
}

export function deleteFloorPlacementsForSpace(spaceId: string): FloorTablePlacement[] {
  const placeId = getCurrentPlaceId()
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  const toDelete = cache.filter((p) => p.spaceId === spaceId)
  floorPlacementsCacheByPlace[placeId] = cache.filter((p) => p.spaceId !== spaceId)
  dispatchFloorUpdated()
  const ops: BatchWriteOp[] = toDelete.map((p) => ({
    type: 'delete' as const,
    ref: floorPlacementDoc(placeId, p.id),
  }))
  commitWriteOps(ops).catch((e) =>
    console.error('Firestore deleteFloorPlacementsForSpace failed', e),
  )
  return floorPlacementsCacheByPlace[placeId]
}

export function deleteFloorPlacementsForTable(tableId: string): FloorTablePlacement[] {
  const placeId = getCurrentPlaceId()
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  const toDelete = cache.filter((p) => p.tableId === tableId)
  floorPlacementsCacheByPlace[placeId] = cache.filter((p) => p.tableId !== tableId)
  dispatchFloorUpdated()
  const ops: BatchWriteOp[] = toDelete.map((p) => ({
    type: 'delete' as const,
    ref: floorPlacementDoc(placeId, p.id),
  }))
  commitWriteOps(ops).catch((e) =>
    console.error('Firestore deleteFloorPlacementsForTable failed', e),
  )
  return floorPlacementsCacheByPlace[placeId]
}

/** Replace every placement on one space. Returns write ops and does not dispatch. */
export function replaceCachedPlacementsForSpace(
  spaceId: string,
  next: FloorTablePlacement[],
): BatchWriteOp[] {
  const placeId = getCurrentPlaceId()
  const cache = floorPlacementsCacheByPlace[placeId] ?? []
  const existing = cache.filter((p) => p.spaceId === spaceId)
  const keep = cache.filter((p) => p.spaceId !== spaceId)
  const normalized = next.map((p) => ({ ...p, spaceId }))
  floorPlacementsCacheByPlace[placeId] = [...keep, ...normalized]
  const nextIds = new Set(normalized.map((p) => p.id))
  const ops: BatchWriteOp[] = []
  for (const p of existing) {
    if (!nextIds.has(p.id)) ops.push({ type: 'delete', ref: floorPlacementDoc(placeId, p.id) })
  }
  for (const p of normalized) {
    ops.push({
      type: 'set',
      ref: floorPlacementDoc(placeId, p.id),
      data: stripUndefined(p) as Record<string, unknown>,
    })
  }
  return ops
}

export function isFloorPlacementsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in floorPlacementsCacheByPlace
}

export function clearFloorPlacementsCache(): void {
  if (floorPlacementsUnsubscribe) {
    floorPlacementsUnsubscribe()
    floorPlacementsUnsubscribe = null
  }
  Object.keys(floorPlacementsCacheByPlace).forEach((k) => delete floorPlacementsCacheByPlace[k])
}
